import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { onIdTokenChanged, signOut, type User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "@/services/config";
import { userService } from "@/services";
import type { UserRecord } from "@/services";

export type UserData = UserRecord & { uid?: string; createdAt?: any };

interface UserContextType {
  user: User | null;
  userData: UserData | null;
  excludedUserIds: string[];
  loading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  userData: null,
  excludedUserIds: [],
  loading: true,
  logout: async () => {},
  refreshUser: async () => {},
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const currentUidRef = useRef<string | null>(null);

  const isPermissionDenied = (err: any) =>
    err?.code?.includes("permission-denied");

  const forceLogout = async () => {
    try {
      await signOut(auth);
      await userService.logout();
      setUser(null);
      setUserData(null);
      setExcludedUserIds([]);
    } catch (error) {
      console.error("Erro ao forçar logout:", error);
    }
  };

  const fetchUserData = async (uid: string, retryCount = 0) => {
    if (currentUidRef.current !== uid) return;

    try {
      const userDocSnap = await getDoc(doc(db, "users", uid));

      if (currentUidRef.current !== uid) return;

      if (userDocSnap.exists()) {
        const data = userDocSnap.data() as UserData;

        if (data.closeAccount) {
          await forceLogout();
          return;
        }

        setUserData(data);
      }
    } catch (error) {
      if (isPermissionDenied(error)) {
        if (retryCount < 2) {
          await new Promise((r) => setTimeout(r, 1500));
          return fetchUserData(uid, retryCount + 1);
        } else {
          console.warn(
            "Permissão negada persistente no perfil. Forçando logout de segurança.",
          );
          await forceLogout();
        }
      } else {
        console.error("Erro inesperado ao buscar dados do usuário:", error);
      }
    }
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      currentUidRef.current = auth.currentUser.uid;
      await fetchUserData(auth.currentUser.uid);
    }
  };

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      setLoading(true);

      setUser(currentUser);
      currentUidRef.current = currentUser ? currentUser.uid : null;

      if (currentUser) {
        await fetchUserData(currentUser.uid);
      } else {
        setUserData(null);
        setExcludedUserIds([]);
      }

      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, "users_blocked"),
      where("blockerId", "==", user.uid),
    );

    return onSnapshot(
      q,
      (snap) => {
        const ids = snap.docs
          .map((d) => d.data()?.blockedId)
          .filter((id) => id && id !== user.uid);
        setExcludedUserIds(Array.from(new Set(ids)));
      },
      async (error) => {
        console.error("Erro no snapshot de bloqueio:", error);
        if (isPermissionDenied(error)) {
          await forceLogout();
        }
      },
    );
  }, [user?.uid]);

  return (
    <UserContext.Provider
      value={{
        user,
        userData,
        excludedUserIds,
        loading,
        logout: forceLogout,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
